import { requestApi } from './api';
import {
  addPresignedParams,
  type BlobCommandOptions,
  BlobError,
  constructBlobUrl,
  getApiUrl,
  isUrl,
  type PresignedUrlPayload,
  parseStoreIdFromDelegationToken,
} from './helpers';
import {
  buildPresignCanonicalQueryEntries,
  PRESIGN_CANONICAL_QUERY_KEYS,
} from './presign-query-params';

/**
 * Operations that may be encoded in a delegation token (e.g. read: `get` /
 * `head` for blob object reads, write: `put` for presigned control-plane writes
 * — both single-object `PUT`, destructive: `delete` for presigned control-plane
 * `DELETE /?pathname=…`). `head` shares the GET URL shape (blob object host)
 * and is distinguished only by the HTTP method and `operation=head` in the
 * canonical signing string.
 */
export type DelegationOperation = 'get' | 'head' | 'put' | 'delete';

/**
 * Result of `issueSignedToken` — the same values returned from `POST /signed-token` on
 * the Blob API. Use with {@link presignUrl} to obtain `{ presignedUrl }` for GET/HEAD,
 * presigned `PUT`, presigned multipart `POST`
 * without a bearer token when verified by the CDN.
 */
export interface IssuedSignedToken {
  /**
   * Encodes delegation scope (pathname, allowed operations, expiry) and a store-level
   * HMAC, as issued by the API.
   */
  delegationToken: string;
  /**
   * Per-issuance HMAC key: `HMAC-SHA256(blobSigningSecret, delegationToken)` in base64url
   * form. The SDK uses this as the HMAC key when signing a concrete blob URL
   * (the CDN re-derives the same value from the delegation token and store secret).
   */
  clientSigningToken: string;
  /** Time after which the delegation (and any presigned URLs) must be rejected, in ms since epoch. */
  validUntil: number;
}

/**
 * Options for {@link issueSignedToken}.
 */
export type IssueSignedTokenOptions = BlobCommandOptions & {
  /**
   * Blob object pathname to scope the token to, e.g. `media/photo.png`.
   * Use `"*"` to allow any pathname in the store. When omitted, the API defaults
   * to a whole-store `"*"` wildcard.
   */
  pathname?: string;
  /**
   * Allowed operations (e.g. `get` / `head` for reads to `*.blob.vercel-storage.com`,
   * `put` for presigned control-plane `PUT` and multipart `POST /mpu`,
   * When omitted, the API defaults to read (`get`) only.
   */
  operations?: DelegationOperation[];
  /**
   * Absolute delegation expiry (ms since epoch). Must be after `now`. When omitted, the API uses `now + 1 hour`.
   */
  validUntil?: number;

  allowedContentTypes?: string[];

  maximumSizeInBytes?: number;
};

interface IssuedSignedTokenResponse {
  delegationToken: string;
  clientSigningToken: string;
  validUntil: number;
}

function assertIssueSignedTokenValidUntilOption(validUntil: number): void {
  const now = Date.now();
  if (
    typeof validUntil !== 'number' ||
    !Number.isInteger(validUntil) ||
    !Number.isFinite(validUntil)
  ) {
    throw new BlobError(
      '`issueSignedToken`: validUntil must be an integer milliseconds timestamp.',
    );
  }
  if (validUntil <= now) {
    throw new BlobError(
      '`issueSignedToken`: validUntil must be in the future.',
    );
  }
}

/**
 * Requests short-lived signed-token material from the Blob control API
 * (`POST /signed-token`). Use OIDC (`VERCEL_OIDC_TOKEN` + `storeId` / `BLOB_STORE_ID`)
 * or a read–write token like other SDK control-plane calls. Client (browser) tokens
 * are not allowed by the server for this operation.
 */
export async function issueSignedToken(
  options: IssueSignedTokenOptions,
): Promise<IssuedSignedToken> {
  if (!options) {
    throw new BlobError('`issueSignedToken` requires an options object');
  }

  const body: Record<string, unknown> = {};
  if (options.pathname !== undefined) {
    body.pathname = options.pathname;
  }
  if (options.operations !== undefined) {
    if (options.operations.length === 0) {
      throw new BlobError('`operations` must be a non-empty array if provided');
    }
    body.operations = dedupeOps(options.operations);
  }
  if (options.validUntil !== undefined) {
    assertIssueSignedTokenValidUntilOption(options.validUntil);
    body.validUntil = options.validUntil;
  }
  if (options.maximumSizeInBytes !== undefined) {
    body.maximumSizeInBytes = options.maximumSizeInBytes;
  }
  if (options.allowedContentTypes !== undefined) {
    body.allowedContentTypes = options.allowedContentTypes;
  }

  return requestApi<IssuedSignedTokenResponse>(
    '/signed-token',
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      signal: options.abortSignal,
    },
    options,
  );
}

function dedupeOps(
  operations: readonly DelegationOperation[],
): DelegationOperation[] {
  return Array.from(new Set(operations));
}

interface DecodedDelegationPayload {
  storeId: string;
  ownerId: string;
  pathname: string;
  operations: string[];
  validUntil: number;
  iat: number;
  maximumSizeInBytes?: number;
  allowedContentTypes?: string[];
}

function base64UrlDecodeToString(segment: string): string {
  let base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padding = 4 - (base64.length % 4);
  if (padding !== 4) {
    base64 += '='.repeat(padding);
  }
  if (typeof atob === 'function') {
    return atob(base64);
  }
  if (typeof Buffer !== 'undefined') {
    // eslint-disable-next-line no-restricted-globals
    return Buffer.from(base64, 'base64').toString('utf8');
  }
  throw new BlobError('Cannot decode base64: no atob or Buffer available.');
}

function tryDecodePayload(
  delegationToken: string,
): DecodedDelegationPayload | null {
  const dot = delegationToken.indexOf('.');
  if (dot < 0) {
    return null;
  }
  const payloadSeg = delegationToken.slice(0, dot);
  try {
    return JSON.parse(
      base64UrlDecodeToString(payloadSeg),
    ) as DecodedDelegationPayload;
  } catch {
    return null;
  }
}

/**
 * @internal
 */
function uint8ToBase64(bytes: Uint8Array): string {
  if (typeof Buffer !== 'undefined') {
    // eslint-disable-next-line no-restricted-globals
    return Buffer.from(
      bytes.buffer,
      bytes.byteOffset,
      bytes.byteLength,
    ).toString('base64');
  }
  let s = '';
  for (let i = 0; i < bytes.length; i++) {
    s += String.fromCharCode(bytes[i]!);
  }
  return btoa(s);
}

/**
 * HMAC-SHA256 with the `clientSigningToken` string as the UTF-8 key; signature
 * digest is base64url (no padding), i.e. `vercel-blob-signature`.
 * Uses the Web Crypto API (Node 20+, browsers, Edge).
 */
async function hmacSha256Base64Url(key: string, data: string): Promise<string> {
  if (!globalThis.crypto?.subtle) {
    throw new BlobError(
      'HMAC is not available: expected globalThis.crypto.subtle (Node 20+ or a modern browser).',
    );
  }
  const enc = new TextEncoder();
  const cryptoKey = await globalThis.crypto.subtle.importKey(
    'raw',
    enc.encode(key),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const buf = await globalThis.crypto.subtle.sign(
    'HMAC',
    cryptoKey,
    enc.encode(data),
  );
  return toBase64Url(uint8ToBase64(new Uint8Array(buf)));
}

/**
 * @internal
 */
function toBase64Url(base64: string): string {
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function normalizeStoreId(storeId: string): string {
  const lowercase = storeId.toLowerCase();
  return lowercase.startsWith('store_')
    ? lowercase.slice('store_'.length)
    : lowercase;
}

/**
 * Presign URL options for {@link presignUrl} when `operation` is `get`.
 * Only `validUntil` is honored for these operations; upload-only fields are rejected at the type level.
 */
export type PresignGetUrlOptions = {
  operation: 'get';

  pathname: string;
  /**
   * Absolute URL expiry (ms since epoch), capped to the delegation `validUntil`.
   * Omitted on the wire when equal to the delegation ceiling (server defaults to delegation).
   */
  validUntil?: number;
  /**
   * Whether the presigned URL may be served from CDN cache. When false, a
   * `cache=0` query param is appended so fetches read the latest content
   * directly from origin storage. The CDN only supports the bypass for
   * private blobs, so it's ignored for public ones. The param is not part
   * of the signed payload: whoever holds the URL can add or remove it.
   * @defaultValue true
   */
  useCache?: boolean;
};

/**
 * Presign URL options for {@link presignUrl} when `operation` is `head`.
 * Mirrors {@link PresignGetUrlOptions}; the CDN URL is identical, the HTTP
 * method differentiates, and the canonical signing string carries
 * `operation=head` so a GET-signed URL cannot be replayed for HEAD.
 */
export type PresignHeadUrlOptions = {
  operation: 'head';

  pathname: string;

  validUntil?: number;
};

/**
 * Presign URL options for {@link presignUrl} when `operation` is `put` (single `PUT` or multipart `POST`).
 * Serialized as individual `vercel-blob-*` query params (see {@link PRESIGN_CANONICAL_QUERY_KEYS}).
 */
export type PresignPutUrlOptions = {
  operation: 'put';

  pathname: string;

  validUntil?: number;

  allowedContentTypes?: string[];

  maximumSizeInBytes?: number;

  onUploadCompleted?: {
    callbackUrl: string;
    tokenPayload?: string;
  };

  allowOverwrite?: boolean;

  addRandomSuffix?: boolean;

  cacheControlMaxAge?: number;

  ifMatch?: string;
};

/**
 * Presign URL options for {@link presignUrl} when `operation` is `delete`.
 * Targets the control-plane `DELETE /?pathname=…` route — mirrors the PUT
 * presign URL shape, signed with `operation=delete` so the HTTP method
 * differentiates without changing the URL path. Only `validUntil` and
 * `ifMatch` are honored; upload-only fields are rejected at the type level.
 */
export type PresignDeleteUrlOptions = {
  operation: 'delete';

  pathname: string;

  validUntil?: number;

  ifMatch?: string;
};

export type PresignUrlOptions =
  | PresignGetUrlOptions
  | PresignHeadUrlOptions
  | PresignPutUrlOptions
  | PresignDeleteUrlOptions;

/**
 * Builds the payload for a presigned URL
 */
export async function presign(
  signedToken: Pick<
    IssuedSignedToken,
    'clientSigningToken' | 'delegationToken'
  >,
  options: PresignUrlOptions,
): Promise<PresignedUrlPayload> {
  if (!signedToken?.clientSigningToken || !signedToken?.delegationToken) {
    throw new BlobError(
      '`clientSigningToken` and `delegationToken` from `issueSignedToken` are required.',
    );
  }

  const scope = tryDecodePayload(signedToken.delegationToken);
  if (!scope) {
    throw new BlobError('Invalid or unreadable `delegationToken` payload.');
  }

  const p = scope.pathname;
  if (p && p !== '*') {
    if (options.pathname !== p) {
      throw new BlobError(
        `Blob path does not match the signed token scope; expected \`${p}\`, got \`${options.pathname}\`.`,
      );
    }
  }
  if (Number.isFinite(scope.validUntil) && Date.now() > scope.validUntil) {
    throw new BlobError(
      'The signed delegation has expired; issue a new token first.',
    );
  }

  if (options.operation === 'get' && !scope.operations?.includes('get')) {
    throw new BlobError(
      'The delegation token is not valid for `GET` requests. Include `"get"` in `operations` when calling `issueSignedToken`.',
    );
  }
  if (options.operation === 'head' && !scope.operations?.includes('head')) {
    throw new BlobError(
      'The delegation token is not valid for `HEAD` requests. Include `"head"` in `operations` when calling `issueSignedToken`.',
    );
  }
  if (options.operation === 'put' && !scope.operations?.includes('put')) {
    throw new BlobError(
      'The delegation token is not valid for presigned write requests. Include `"put"` in `operations` when calling `issueSignedToken`.',
    );
  }
  if (options.operation === 'delete' && !scope.operations?.includes('delete')) {
    throw new BlobError(
      'The delegation token is not valid for presigned delete requests. Include `"delete"` in `operations` when calling `issueSignedToken`.',
    );
  }

  const delegationForOptions = {
    validUntil: scope.validUntil,
    maximumSizeInBytes: scope.maximumSizeInBytes,
    allowedContentTypes: scope.allowedContentTypes,
  };

  let presignEntries: [string, string][];
  try {
    presignEntries = buildPresignCanonicalQueryEntries({
      operation: options.operation,
      delegation: delegationForOptions,
      urlOptions: options,
      nowMs: Date.now(),
    });
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    throw new BlobError(msg);
  }

  const canonical = canonicalString(
    options.pathname,
    presignEntries,
    options.operation,
  );
  const signature = await hmacSha256Base64Url(
    signedToken.clientSigningToken,
    canonical,
  );

  return {
    delegationToken: signedToken.delegationToken,
    signature,
    params: Object.fromEntries(presignEntries),
  };
}

function buildPresignedGetUrl(
  pathnameOrUrl: string,
  presignedUrlPayload: PresignedUrlPayload,
  options: {
    access: 'public' | 'private';
    useCache?: boolean;
  },
): string {
  const storeId = parseStoreIdFromDelegationToken(
    presignedUrlPayload.delegationToken,
  );
  let blobUrl = isUrl(pathnameOrUrl)
    ? pathnameOrUrl
    : constructBlobUrl(storeId, pathnameOrUrl, options.access);

  // Same gating as get(): the CDN only supports the cache bypass for private
  // blobs. The param is deliberately outside the signed payload, so adding it
  // here doesn't affect signature verification.
  if (options.useCache === false && options.access === 'private') {
    const url = new URL(blobUrl);
    url.searchParams.set('cache', '0');
    blobUrl = url.toString();
  }

  return addPresignedParams(blobUrl, presignedUrlPayload);
}

function buildPresignedPutUrl(
  pathname: string,
  presignedUrlPayload: PresignedUrlPayload,
): string {
  const params = new URLSearchParams({ pathname });
  const apiUrl = getApiUrl(`/?${params.toString()}`);
  return addPresignedParams(apiUrl, presignedUrlPayload);
}

function buildPresignedDeleteUrl(
  pathname: string,
  presignedUrlPayload: PresignedUrlPayload,
): string {
  const params = new URLSearchParams({ pathname });
  const apiUrl = getApiUrl(`/?${params.toString()}`);
  return addPresignedParams(apiUrl, presignedUrlPayload);
}

/** Result of {@link presignUrl}: a ready-to-fetch blob object URL or control-plane upload URL. */
export type PresignUrlResult = {
  presignedUrl: string;
};

/**
 * Builds a presigned URL for `GET` / `HEAD` (blob object host), `PUT` / multipart (control API),
 * or `DELETE` (`DELETE /?pathname=…` on the control API — mirrors the PUT URL shape; the
 * HTTP method discriminates, and the canonical signing string includes `operation=delete`).
 * `HEAD` reuses the GET URL shape against the blob object host; `operation=head` in the
 * canonical string prevents a GET-signed URL from being replayed as a HEAD.
 */
export async function presignUrl(
  signedToken: Pick<
    IssuedSignedToken,
    'clientSigningToken' | 'delegationToken'
  >,
  options: PresignUrlOptions & { access: 'public' | 'private' },
): Promise<PresignUrlResult> {
  const payload = await presign(signedToken, options);

  if (options.operation === 'get' || options.operation === 'head') {
    return {
      presignedUrl: buildPresignedGetUrl(options.pathname, payload, options),
    };
  }
  if (options.operation === 'put') {
    return {
      presignedUrl: buildPresignedPutUrl(options.pathname, payload),
    };
  }
  if (options.operation === 'delete') {
    return {
      presignedUrl: buildPresignedDeleteUrl(options.pathname, payload),
    };
  }

  throw new BlobError(`Unknown operation`);
}

/** @internal Exported for presign URL contract tests (must match proxy / api-blob). */
export function canonicalString(
  pathname: string,
  presignEntries: [string, string][],
  operation: DelegationOperation,
): string {
  const lines: string[] = [`operation=${operation}`, `pathname=${pathname}`];
  for (const k of PRESIGN_CANONICAL_QUERY_KEYS) {
    const v = presignEntries.find(([key]) => key === k)?.[1];
    if (v) {
      lines.push(`${k}=${v}`);
    }
  }
  lines.sort((a, b) => compareUtf8(a, b));
  return lines.join('\n');
}

const utf8Encoder = new TextEncoder();

/**
 * Lexicographic order of UTF-8 bytewise (matches Go `string` comparison and
 * `url.Values` key / pair ordering in practice).
 * @internal
 */
function compareUtf8(a: string, b: string): number {
  const ab = utf8Encoder.encode(a);
  const bb = utf8Encoder.encode(b);
  const n = Math.min(ab.length, bb.length);
  for (let i = 0; i < n; i++) {
    const d = (ab[i]! - bb[i]!) as number;
    if (d !== 0) {
      return d;
    }
  }
  return ab.length - bb.length;
}
